/**
 * Orientative severity classification for CVAI and IC, reusing the same scales as the
 * cranial asymmetry sheet (`cranialSeverity.js`) and the same chip appearance
 * (`CranialSeverityChip`). This is not a diagnosis: it only labels the indices already
 * computed by `computeMeasurements` with the sheet's severity bands.
 *
 * The CVAI classified here is the tool's own value (protocol v2, shorter diagonal as the
 * denominator). The sheet still computes its CVAI over the longer diagonal, so the two
 * percentages can differ slightly for the same head until the sheet is aligned.
 */
import {
  classifyApBpPercentage,
  classifyPlagiocephalyPercentage,
} from '../../../appointment/components/cranialAsymmetry/cranialSeverity';

const CVAI_SCALE_LABEL = 'Escala de plagiocefalia (CVAI)';
const CI_SCALE_LABEL = 'Escala AP/BP (índice cefálico)';

const toClassification = (severity, scale) => ({
  label: severity.severity,
  color: severity.color,
  textColor: severity.textColor,
  scale,
});

/**
 * Classifies CVAI and IC from the indices returned by `computeMeasurements`.
 * Both indices are unitless, so px and mm give the same classification.
 * @param {object} measurements Result of `computeMeasurements` (lib/cranialGeometry.js).
 * @returns {{cvai:{label:string,color:string,textColor:string,scale:string},
 *   ci:{label:string,color:string,textColor:string,scale:string}}|null} `null` when the
 *   measurements are missing or not `ok`.
 */
export function classifyMeasurements(measurements) {
  if (!measurements || !measurements.ok || !measurements.indices) return null;

  const { cvai, ci } = measurements.indices;

  return {
    cvai: toClassification(classifyPlagiocephalyPercentage(cvai), CVAI_SCALE_LABEL),
    ci: toClassification(classifyApBpPercentage(ci), CI_SCALE_LABEL),
  };
}

export default classifyMeasurements;
